import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

/**
 * Premium Error Display Component
 * Shows a visually appealing error message with a retry option
 * 
 * @param {string} message - Error message to display
 * @param {function} onRetry - Function to call when user clicks retry
 */
const ErrorDisplay = ({
    message = "Something went wrong while loading the data.",
    onRetry
}) => {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '400px',
                width: '100%',
                bgcolor: '#fafafa',
                borderRadius: '16px',
                border: '1px dashed #e0e0e0',
                p: 4,
                textAlign: 'center'
            }}
        >
            <Box
                sx={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    bgcolor: '#fff1f1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3,
                    boxShadow: '0px 4px 10px rgba(234, 67, 53, 0.1)'
                }}
            >
                <WarningAmberRoundedIcon sx={{ fontSize: '40px', color: '#ea4335' }} />
            </Box>

            <Typography
                variant="h6"
                sx={{
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    fontWeight: 600,
                    color: '#333',
                    mb: 1
                }}
            >
                Oops! Data Load Failed
            </Typography>

            <Typography
                sx={{
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    color: '#666',
                    maxWidth: '400px',
                    mb: 4,
                    lineHeight: 1.6
                }}
            >
                {message}. This could be due to a temporarily network issue or server error.
            </Typography>

            {onRetry && (
                <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={onRetry}
                    sx={{
                        bgcolor: '#007bff',
                        color: 'white',
                        px: 4,
                        py: 1.2,
                        borderRadius: '12px',
                        textTransform: 'none',
                        fontSize: '16px',
                        fontWeight: 500,
                        boxShadow: '0px 4px 12px rgba(0, 123, 255, 0.25)',
                        '&:hover': {
                            bgcolor: '#0056b3',
                            boxShadow: '0px 6px 16px rgba(0, 123, 255, 0.35)',
                        },
                    }}
                >
                    Try Again
                </Button>
            )}
        </Box>
    );
};

export default ErrorDisplay;
